import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLoginModal } from "../components/LoginModalProvider";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { supabase } from "../lib/supabase";
import Footer from "../components/Footer";

/* ═══════════════════════════════════════════════════════════
   SCHEMA THIS FILE ASSUMES:
     pro_services: id, pro_id, name, description, price,
       duration_minutes, category, status, created_at,
       headline (NEW, nullable), included_items (NEW, text[]),
       excluded_items (NEW, text[]), prep_notes (NEW, nullable),
       team_size (NEW, int, nullable), supplies_included (NEW, bool, nullable)
       (this is the table ProDashboardHome's ServiceModal writes
       to — NOT the `services` table ProProfilePage.jsx reads
       from. Those look like two different tables; confirm which
       is real before this page and ProProfilePage disagree in
       production.)
     service_packages (NEW, optional): id, service_id, name, price,
       description, sort_order — if a service has rows here, the
       page shows tiered pricing instead of the single price on
       pro_services. If empty, single price is used unchanged.
     service_media: id, service_id, url, type ('image'|'video'),
       sort_order, is_cover, stage (NEW, nullable: 'before'|'after'),
       pair_group (NEW, nullable, groups a before/after pair)
     pro_profiles: id, avg_rating, review_count, completed_jobs,
       success_score, response_minutes, response_rate, repeat_rate,
       availability, verified, background_checked, member_since,
       category, badge, jirani_name
     pro_availability (referenced, schema per prior work): pro_id,
       day_of_week / date, start_time, end_time — used only to
       compute a real "next available slot"; if the query fails or
       returns nothing usable, we fall back to the plain
       available/busy dot instead of guessing a time.
     profiles: full_name, avatar_url, location
     reviews: id, rating, comment, created_at, customer_id, pro_id
       (matches ProProfilePage.jsx's `comment` column — note
       ReviewsRatings.jsx assumes `review_text` instead; same
       fork as the services/pro_services split above)
     job_requests: same insert shape as ProProfilePage's
       BookingModal (user_id, pro_id, service_id, description,
       status, scheduled_time, location, notes, estimated_price,
       payment_status)

   NOTE ON DATA HONESTY: included_items / excluded_items / prep_notes /
   team_size / supplies_included / service_packages / before-after
   media are all OPTIONAL and pro-authored. If a pro hasn't filled
   them in yet, the corresponding section is simply not rendered —
   we never fabricate checklist items, tiers, or before/after pairs
   to fill space. Wiring the input side of these fields into
   ProDashboardHome's ServiceModal is a separate, still-open task.
═══════════════════════════════════════════════════════════ */

if (!document.getElementById("__pp_fonts")) {
  const l = document.createElement("link");
  l.id  = "__pp_fonts";
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap";
  document.head.appendChild(l);
}

/* ── Tokens — identical to ProProfilePage.jsx ── */
const T = {
  green: "#1e9254", greenDark: "#166b3e", greenDeep: "#0d2e1a",
  greenTint: "#f0faf4", greenBd: "#c6e8d4", greenGlow: "rgba(30,146,84,0.16)",
  bg: "#f5f6f8", white: "#ffffff", border: "#e5e7eb", borderFaint: "#f0f2f5", hoverBg: "#f9fafb",
  text: "#0d1117", textMid: "#374151", sub: "#6b7280", faint: "#9ca3af",
  amber: "#92400e", amberBg: "#fffbeb", amberBd: "#fde68a",
  red: "#dc2626", redTint: "#fef2f2", redBd: "#fecaca",
  blue: "#1d4ed8", blueTint: "#eff6ff", blueBd: "#bfdbfe",
  gold: "#f59e0b",
  font: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
  serif: "'Instrument Serif', Georgia, serif",
  r8:"8px", r10:"10px", r12:"12px", r14:"14px", r16:"16px", r20:"20px", rFull:"9999px",
  s1: "0 1px 3px rgba(0,0,0,0.06)",
  s2: "0 4px 16px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)",
  s3: "0 8px 32px rgba(0,0,0,0.09), 0 2px 8px rgba(0,0,0,0.05)",
  s4: "0 16px 48px rgba(0,0,0,0.11), 0 4px 12px rgba(0,0,0,0.06)",
  sGreen: "0 4px 20px rgba(30,146,84,0.22)",
};

const SERVICE_CATEGORIES = [
  { key: "cleaning",             label: "House Cleaning" },
  { key: "plumbing",             label: "Plumbing" },
  { key: "electronics-repair",   label: "Electronics Repair" },
  { key: "hair-beauty",          label: "Hair & Beauty" },
  { key: "home-tutoring",        label: "Home Tutoring" },
  { key: "photography",          label: "Photography" },
  { key: "electrical",           label: "Electrical Work" },
  { key: "gardening",            label: "Gardening" },
  { key: "carpentry-furniture",  label: "Carpentry & Furniture" },
  { key: "moving",               label: "Moving Services" },
  { key: "childcare",            label: "Childcare" },
  { key: "fitness",              label: "Personal Training" },
  { key: "auto-repair",          label: "Auto Repair" },
  { key: "errands-delivery",     label: "Errands & Delivery" },
  { key: "chef",                 label: "Private Chef" },
];
const categoryLabel = (key) => SERVICE_CATEGORIES.find(c => c.key === key)?.label || "Home Services";

const KENYAN_AREAS = [
  "Westlands","Kilimani","Lavington","Karen","Kileleshwa","Runda","Parklands",
  "South B","South C","Langata","Kasarani","Ruaka","Kikuyu","Rongai",
  "Thika Road","Upperhill","Gigiri","Muthaiga","Ngong Road","CBD","Embakasi",
  "Donholm","Buruburu","Roysambu","Kahawa",
];

function buildRelatedSearches(category, city) {
  const areas = [city, ...KENYAN_AREAS].filter(Boolean);
  const terms = [];
  SERVICE_CATEGORIES.forEach(cat => {
    areas.slice(0, cat.key === category ? 12 : 4).forEach(area => {
      terms.push({ label: `${cat.label.toLowerCase()} in ${area}`, category: cat.key, area });
    });
  });
  return terms;
}

/* ── Currency — single source of truth, Kenyan shillings everywhere ── */
function fmtKsh(n) {
  const v = Number(n || 0);
  return `Ksh ${v.toLocaleString("en-KE")}`;
}

/* ── Demo fallback so /service/demo renders standalone ── */
const DEMO_SERVICE = {
  id: "demo", pro_id: "demo_pro",
  name: "Deep Clean",
  headline: "Premium Deep House Cleaning Service",
  category: "cleaning", status: "approved",
  price: 2500, duration_minutes: 360,
  description: "Full home deep clean including inside appliances, cupboards, and windows. Recommended every one to two months. I bring all my own eco-friendly products and equipment — you only need to provide access to water and electricity.",
  included_items: ["Kitchen cleaning", "Bathroom sanitization", "Bedroom cleaning", "Living room dusting", "Window cleaning", "Appliance exterior cleaning", "Floor scrubbing and mopping"],
  excluded_items: ["Pest control", "Painting", "Furniture moving"],
  prep_notes: "Please ensure access to water and electricity, secure valuables, and share parking instructions if needed.",
  team_size: 1,
  supplies_included: true,
  pro: {
    id: "demo_pro", avg_rating: 4.9, review_count: 87, completed_jobs: 628,
    success_score: 98, response_minutes: 8, response_rate: 98, repeat_rate: 72,
    availability: "available", verified: true, background_checked: true,
    member_since: "March 2021", category: "cleaning", badge: "Top Rated", jirani_name: "Mary W.",
    profiles: { full_name: "Mary Wanjiku", avatar_url: null, location: "Westlands, Nairobi" },
  },
};
const DEMO_MEDIA = [
  { id: "m1", url: "https://picsum.photos/seed/jiranisvc1/900/900", type: "image", is_cover: true },
  { id: "m2", url: "https://picsum.photos/seed/jiranisvc2/900/900", type: "image", is_cover: false },
  { id: "m3", url: "https://picsum.photos/seed/jiranisvc3/900/900", type: "image", is_cover: false },
  { id: "m4", url: "https://www.w3schools.com/html/mov_bbb.mp4", type: "video", is_cover: false, poster: "https://picsum.photos/seed/jiranisvc4/900/900" },
];
const DEMO_REVIEWS = [
  { id: "r1", rating: 5, comment: "Mary was incredible — my apartment has never looked this clean.", created_at: new Date(Date.now()-2*86400000).toISOString(), reviewer: { full_name: "Amina K.", avatar_url: null } },
  { id: "r2", rating: 5, comment: "Got my full deposit back thanks to her attention to detail.", created_at: new Date(Date.now()-9*86400000).toISOString(), reviewer: { full_name: "David M.", avatar_url: null } },
];

/* ── Supabase helpers ── */
async function fetchService(serviceId) {
  const { data, error } = await supabase
    .from("pro_services")
    .select(`
      id, pro_id, name, headline, description, price, duration_minutes, category, status, created_at,
      included_items, excluded_items, prep_notes, team_size, supplies_included,
      pro:pro_profiles!pro_id (
        id, avg_rating, review_count, completed_jobs, success_score,
        response_minutes, response_rate, repeat_rate, availability,
        verified, background_checked, member_since, category, badge, jirani_name,
        profiles!user_id ( full_name, avatar_url, location )
      )
    `)
    .eq("id", serviceId)
    .single();
  if (error) throw error;
  return data;
}

async function fetchServiceMedia(serviceId) {
  const { data, error } = await supabase
    .from("service_media")
    .select("id, url, type, sort_order, is_cover, stage, pair_group")
    .eq("service_id", serviceId)
    .order("sort_order", { ascending: true });
  if (error) throw error;
  return data || [];
}

/* Optional tiered pricing. Returns [] (not fake tiers) if the pro
   hasn't set any up — callers must treat [] as "use single price". */
async function fetchServicePackages(serviceId) {
  try {
    const { data, error } = await supabase
      .from("service_packages")
      .select("id, name, price, description, sort_order")
      .eq("service_id", serviceId)
      .order("sort_order", { ascending: true });
    if (error) throw error;
    return data || [];
  } catch {
    // Table may not exist yet in every environment — fail soft, not fake.
    return [];
  }
}

async function fetchCoverMediaMap(serviceIds) {
  if (!serviceIds.length) return {};
  try {
    const { data } = await supabase.from("service_media").select("service_id, url, type").in("service_id", serviceIds).eq("is_cover", true);
    const map = {};
    (data || []).forEach(m => { map[m.service_id] = m; });
    return map;
  } catch { return {}; }
}

async function fetchListings({ category, excludeProId, excludeServiceId, matchProId, limit = 18 }) {
  let q = supabase
    .from("pro_services")
    .select(`
      id, pro_id, name, price, category, status,
      pro:pro_profiles!pro_id ( avg_rating, review_count, availability, badge, profiles!user_id ( full_name, avatar_url, location ) )
    `)
    .eq("status", "approved")
    .limit(limit);
  if (matchProId) q = q.eq("pro_id", matchProId);
  else if (category) q = q.eq("category", category);
  if (excludeProId) q = q.neq("pro_id", excludeProId);
  if (excludeServiceId) q = q.neq("id", excludeServiceId);

  const { data, error } = await q;
  if (error) throw error;
  const rows = data || [];
  const coverMap = await fetchCoverMediaMap(rows.map(r => r.id));
  return rows.map(r => ({
    id: r.id,
    name: r.name,
    price: r.price,
    proId: r.pro_id,
    proName: r.pro?.profiles?.full_name || "JiraniPro",
    area: r.pro?.profiles?.location || null,
    rating: r.pro?.avg_rating ?? null,
    reviews: r.pro?.review_count ?? 0,
    badge: r.pro?.badge || null,
    img: coverMap[r.id]?.url || null,
  }));
}

async function fetchProReviews(proId) {
  const { data, error } = await supabase
    .from("reviews")
    .select("id, rating, comment, created_at, reviewer:customer_id ( full_name, avatar_url )")
    .eq("pro_id", proId)
    .order("created_at", { ascending: false })
    .limit(20);
  if (error) throw error;
  return data || [];
}

/* Real "next available slot", computed from the pro's own availability
   windows minus anything already booked. If either table is missing or
   the query can't produce a confident answer, we return null and the
   UI falls back to the plain available/busy dot — we do not guess a time. */
async function fetchNextAvailableSlot(proId) {
  try {
    const now = new Date();
    const dow = now.getDay();
    const { data: windows, error: wErr } = await supabase
      .from("pro_availability")
      .select("day_of_week, start_time, end_time")
      .eq("pro_id", proId)
      .in("day_of_week", [dow, (dow + 1) % 7]);
    if (wErr || !windows || !windows.length) return null;

    const todayStr = now.toISOString().split("T")[0];
    const tomorrow = new Date(now.getTime() + 86400000).toISOString().split("T")[0];
    const { data: booked } = await supabase
      .from("job_requests")
      .select("scheduled_time")
      .eq("pro_id", proId)
      .in("status", ["pending", "confirmed"])
      .gte("scheduled_time", `${todayStr}T00:00:00`)
      .lte("scheduled_time", `${tomorrow}T23:59:59`);
    const bookedTimes = new Set((booked || []).map(b => b.scheduled_time));

    const todayWindow = windows.find(w => w.day_of_week === dow);
    if (todayWindow) {
      const [h, m] = todayWindow.start_time.split(":").map(Number);
      const slot = new Date(now);
      slot.setHours(Math.max(h, now.getHours() + 1), m, 0, 0);
      const [endH] = todayWindow.end_time.split(":").map(Number);
      if (slot.getHours() < endH && !bookedTimes.has(slot.toISOString())) {
        return { label: "Today", time: slot };
      }
    }
    const tmrWindow = windows.find(w => w.day_of_week === (dow + 1) % 7);
    if (tmrWindow) {
      const [h, m] = tmrWindow.start_time.split(":").map(Number);
      const slot = new Date(now.getTime() + 86400000);
      slot.setHours(h, m, 0, 0);
      return { label: "Tomorrow", time: slot };
    }
    return null;
  } catch {
    return null;
  }
}

/* Trust Score — a transparent blend of real, existing metrics. Every
   input here already exists on pro_profiles; nothing is invented.
   Shown with its inputs so it's auditable, not a black-box number. */
function computeTrustScore(pro) {
  if (!pro) return null;
  const parts = [];
  let weighted = 0, weightTotal = 0;
  const add = (label, value, weight) => {
    if (value == null) return;
    parts.push({ label, value });
    weighted += value * weight;
    weightTotal += weight;
  };
  add("Rating", pro.avg_rating != null ? (pro.avg_rating / 5) * 100 : null, 0.30);
  add("Jobs completed", pro.completed_jobs != null ? Math.min(100, (pro.completed_jobs / 300) * 100) : null, 0.15);
  add("Repeat clients", pro.repeat_rate != null ? pro.repeat_rate : null, 0.15);
  add("Response rate", pro.response_rate != null ? pro.response_rate : null, 0.15);
  add("Identity verified", pro.verified ? 100 : 0, 0.125);
  add("Background checked", pro.background_checked ? 100 : 0, 0.125);
  if (!weightTotal) return null;
  return { score: Math.round(weighted / weightTotal), inputs: parts };
}

/* ── Utils ── */
const PALETTE = ["#334155","#0369a1","#7c3aed","#92400e","#be185d","#0e7490","#065f46"];
const avatarColor = (n="") => PALETTE[((n.charCodeAt(0)||0)+(n.charCodeAt(1)||0)) % PALETTE.length];
const toInitials  = (n="") => n.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
function fmtDate(iso) { if (!iso) return "TBD"; try { return new Date(iso).toLocaleDateString("en-KE",{weekday:"long",day:"numeric",month:"long",year:"numeric"}); } catch { return iso; } }
function fmtShortDate(iso) { if (!iso) return ""; try { return new Date(iso).toLocaleDateString("en-KE",{day:"numeric",month:"short"}); } catch { return ""; } }
function fmtClock(t) { const [h,m] = t.split(":").map(Number); return `${h>12?h-12:h||12}:${String(m).padStart(2,"0")} ${h>=12?"PM":"AM"}`; }
function fmtDuration(mins) {
  if (!mins) return null;
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins/60), m = mins%60;
  return m ? `${h} hr ${m} min` : `${h} hr${h>1?"s":""}`;
}
function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff/86400000);
  if (days < 1) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-KE",{month:"short",year:"numeric"});
}

/* ── Atoms (matched to ProProfilePage.jsx) ── */
function Avatar({ name="", url, size=44, ring=false }) {
  const [err, setErr] = useState(false);
  const bg = avatarColor(name);
  return (
    <div style={{ width:size, height:size, borderRadius:"50%", flexShrink:0, overflow:"hidden", background:bg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:Math.round(size*0.34), fontWeight:600, color:"#fff", fontFamily:T.font, boxShadow: ring ? `0 0 0 3px ${T.white}, 0 0 0 5px ${T.border}` : "none" }}>
      {url && !err ? <img src={url} alt={name} onError={()=>setErr(true)} style={{width:"100%",height:"100%",objectFit:"cover"}}/> : <span style={{userSelect:"none"}}>{toInitials(name)}</span>}
    </div>
  );
}
function Stars({ n=0, size=12 }) {
  return (
    <span style={{display:"inline-flex",gap:1.5}}>
      {[1,2,3,4,5].map(i=>(
        <svg key={i} width={size} height={size} viewBox="0 0 20 20" fill={i<=Math.round(n)?T.gold:"#e5e7eb"} style={{flexShrink:0}}>
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
        </svg>
      ))}
    </span>
  );
}
function Badge({ children, variant="neutral" }) {
  const map = {
    neutral: { bg:"#f3f4f6", color:T.sub, bd:T.border },
    green:   { bg:T.greenTint, color:T.green, bd:T.greenBd },
    amber:   { bg:T.amberBg, color:T.amber, bd:T.amberBd },
    blue:    { bg:T.blueTint, color:T.blue, bd:T.blueBd },
  };
  const s = map[variant]||map.neutral;
  return <span style={{ display:"inline-flex",alignItems:"center",gap:4, background:s.bg,color:s.color,border:`1px solid ${s.bd}`, borderRadius:T.rFull,padding:"2px 9px", fontSize:11,fontWeight:600,fontFamily:T.font,whiteSpace:"nowrap" }}>{children}</span>;
}
function Btn({ children, v="ghost", size="md", onClick, disabled=false, full=false }) {
  const [hov,setHov] = useState(false);
  const [press,setPress] = useState(false);
  const sz = { sm:{fs:12,h:34,pad:"0 14px",r:T.r8}, md:{fs:13,h:40,pad:"0 18px",r:T.r10}, lg:{fs:14,h:48,pad:"0 24px",r:T.r12} }[size];
  const vs = {
    primary:{ bg:hov?T.greenDark:T.green, col:"#fff", bd:"none", sh:hov?T.sGreen:"0 2px 8px rgba(30,146,84,0.18)" },
    ghost:  { bg:hov?"#f3f4f6":T.white, col:T.textMid, bd:`1px solid ${T.border}`, sh:"none" },
    outline:{ bg:hov?T.greenTint:T.white, col:T.green, bd:`1px solid ${T.greenBd}`, sh:"none" },
    danger: { bg:hov?T.redTint:T.white, col:T.red, bd:`1px solid ${T.redBd}`, sh:"none" },
  };
  const s = vs[v]||vs.ghost;
  return (
    <button onClick={onClick} disabled={disabled}
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>{setHov(false);setPress(false);}}
      onMouseDown={()=>setPress(true)} onMouseUp={()=>setPress(false)}
      style={{ display:"inline-flex",alignItems:"center",justifyContent:"center",gap:7, height:sz.h,padding:sz.pad,borderRadius:sz.r, fontSize:sz.fs,fontWeight:500,fontFamily:T.font, background:s.bg,color:s.col,border:s.bd,boxShadow:s.sh, cursor:disabled?"default":"pointer",opacity:disabled?0.45:1, transform:press?"scale(0.97)":"scale(1)", transition:"all 0.15s ease-out",whiteSpace:"nowrap",outline:"none", minHeight:44,...(full?{width:"100%"}:{}) }}>
      {children}
    </button>
  );
}
function Div({ my=0 }) { return <div style={{height:1,background:T.border,margin:`${my}px 0`}}/>; }

const I = {
  back:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>,
  check: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>,
  checkFat: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>,
  x: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>,
  pin:   <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  cal:   <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>,
  clock: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>,
  msg:   <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
  heart: (f)=><svg width="16" height="16" viewBox="0 0 24 24" fill={f?"#ef4444":"none"} stroke={f?"#ef4444":"currentColor"} strokeWidth="1.8" strokeLinecap="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>,
  chevD: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 9l6 6 6-6"/></svg>,
  chevU: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 15l-6-6-6 6"/></svg>,
  chevL: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>,
  chevR: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>,
  close: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>,
  verified: <svg width="16" height="16" viewBox="0 0 24 24" fill="#3b82f6"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>,
  brief: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>,
  star:  <svg width="12" height="12" viewBox="0 0 20 20" fill="#f59e0b"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>,
  shield: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  mpesa: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 1 0 0 7h5a3.5 3.5 0 1 1 0 7H6"/></svg>,
  card:  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
  user:  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  info:  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
  phone: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.09 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
  play:  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>,
  search:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>,
  flag:  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>,
  share: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>,
  bolt: <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z"/></svg>,
};

/* ─────────────────────────────────────
   EDITORIAL HEADING (matches ProProfilePage)
───────────────────────────────────── */
function EditorialHeading({ eyebrow, title, sub, size="md" }) {
  const { isMobile } = useBreakpoint();
  const sizes = { sm: isMobile?16:18, md: isMobile?19:22 };
  return (
    <div style={{ marginBottom: sub ? 10 : 8 }}>
      {eyebrow && <p style={{ margin:"0 0 3px", fontSize:10, fontWeight:700, color:T.green, textTransform:"uppercase", letterSpacing:"0.09em", fontFamily:T.font }}>{eyebrow}</p>}
      <h2 style={{ margin:0, fontFamily:T.serif, fontStyle:"italic", fontWeight:400, fontSize: sizes[size], color:T.text, letterSpacing:"-0.02em", lineHeight:1.15 }}>{title}</h2>
      {sub && <p style={{ margin:"3px 0 0", fontSize:12.5, color:T.sub, fontFamily:T.font }}>{sub}</p>}
    </div>
  );
}
function Section({ id, eyebrow, title, sub, children }) {
  const { isMobile } = useBreakpoint();
  return (
    <div id={`sec-${id}`} style={{ background:T.white, border:`1.5px solid ${T.border}`, borderRadius:T.r16, padding: isMobile ? "16px 14px" : "18px 20px", marginBottom: isMobile ? 10 : 12, boxShadow:T.s1 }}>
      <EditorialHeading eyebrow={eyebrow} title={title} sub={sub} size="sm" />
      <div style={{ marginTop: 12 }}>{children}</div>
    </div>
  );
}
function Skel({ w="100%", h=16, r=8 }) { return <div style={{width:w,height:h,borderRadius:r,background:"#edeef0",animation:"ppPulse 1.5s ease-in-out infinite"}}/>; }
function PageSkeleton() {
  return (
    <div style={{maxWidth:1120,margin:"0 auto",padding:"20px 20px"}}>
      <div style={{display:"flex",gap:16,marginBottom:20,flexWrap:"wrap"}}>
        <Skel w={340} h={340} r={16}/>
        <div style={{flex:1,minWidth:260,display:"flex",flexDirection:"column",gap:10,paddingTop:4}}>
          <Skel h={24} w={220}/><Skel h={14} w={280}/><Skel h={30} w={140}/><Skel h={44} w={180}/>
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 300px",gap:20}}>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>{[110,180,150].map((h,i)=><Skel key={i} h={h} r={16}/>)}</div>
        <Skel h={400} r={16}/>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────
   PRODUCT GALLERY — the service's own
   photos/videos from service_media.
   FIX: gallery width now matches the
   grid column it lives in (see hero
   grid below), so thumbnails no longer
   overflow into the title/price column.
───────────────────────────────────── */
function ProductGallery({ media, name }) {
  const { isMobile } = useBreakpoint();
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const items = media && media.length ? media : [];
  const current = items[active];

  if (!items.length) {
    return (
      <div style={{ width:"100%", aspectRatio:"1/1", maxWidth:400, borderRadius:T.r16, background:T.borderFaint, border:`1px dashed ${T.border}`, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:8 }}>
        <span style={{ color:T.faint, display:"flex" }}>{I.brief}</span>
        <span style={{ fontSize:12, color:T.faint, fontFamily:T.font }}>No photos yet for this service</span>
      </div>
    );
  }

  return (
    <>
      <div style={{ display:"flex", flexDirection:"column", gap:8, width:"100%" }}>
        <div onClick={()=>setLightbox(active)} style={{
          position:"relative", width:"100%", aspectRatio:"1/1", maxWidth: isMobile ? "none" : 440,
          borderRadius:T.r16, overflow:"hidden", cursor:"pointer", background:"#e5e7eb",
          border:`1px solid ${T.border}`,
        }}>
          {current.type === "video" ? (
            <video src={current.url} poster={current.poster} muted style={{ width:"100%", height:"100%", objectFit:"cover" }} />
          ) : (
            <img src={current.url} alt={name} style={{ width:"100%", height:"100%", objectFit:"cover" }} />
          )}
          {current.type === "video" && (
            <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", background:"rgba(13,17,23,0.22)" }}>
              <div style={{ width:44, height:44, borderRadius:"50%", background:"rgba(255,255,255,0.92)", display:"flex", alignItems:"center", justifyContent:"center", color:T.green }}>{I.play}</div>
            </div>
          )}
          {current.stage && (
            <span style={{ position:"absolute", top:8, left:8, background: current.stage==="after" ? T.green : "rgba(13,17,23,0.65)", color:"#fff", fontSize:10.5, fontWeight:700, fontFamily:T.font, textTransform:"uppercase", letterSpacing:"0.05em", borderRadius:T.rFull, padding:"3px 9px" }}>{current.stage}</span>
          )}
          <div style={{ position:"absolute", bottom:8, right:8, background:"rgba(13,17,23,0.65)", color:"#fff", fontSize:11, fontWeight:600, fontFamily:T.font, borderRadius:T.rFull, padding:"2px 9px" }}>{active+1}/{items.length}</div>
        </div>
        {/* Thumbnails now sit BELOW the main image, never beside it —
            this removes the overflow that was covering the title/price
            column at narrower widths. */}
        <div style={{ display:"flex", gap:8, overflowX:"auto", WebkitOverflowScrolling:"touch" }} className="jp-scroll">
          {items.map((m,i) => (
            <button key={m.id||i} onClick={()=>setActive(i)} style={{
              position:"relative", width:56, height:56, flexShrink:0,
              borderRadius:T.r10, overflow:"hidden", cursor:"pointer", padding:0, background:"#e5e7eb",
              border: active===i ? `2px solid ${T.green}` : `1px solid ${T.border}`,
            }}>
              {m.type === "video" ? <video src={m.url} muted style={{width:"100%",height:"100%",objectFit:"cover"}}/> : <img src={m.url} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>}
              {m.type === "video" && <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", background:"rgba(13,17,23,0.25)" }}><span style={{ color:"#fff", display:"flex", transform:"scale(0.4)" }}>{I.play}</span></div>}
            </button>
          ))}
        </div>
      </div>

      {lightbox !== null && (
        <div onClick={()=>setLightbox(null)} style={{ position:"fixed", inset:0, zIndex:1000, background:"rgba(13,17,23,0.92)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
          <button onClick={()=>setLightbox(null)} style={{ position:"absolute", top:20, right:20, width:40, height:40, borderRadius:"50%", background:"rgba(255,255,255,0.12)", border:"none", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}>{I.close}</button>
          <div onClick={e=>e.stopPropagation()} style={{ maxWidth:760, width:"100%" }}>
            {items[lightbox].type === "video" ? <video src={items[lightbox].url} controls autoPlay style={{ width:"100%", borderRadius:T.r12 }} /> : <img src={items[lightbox].url} alt="" style={{ width:"100%", borderRadius:T.r12 }} />}
          </div>
        </div>
      )}
    </>
  );
}

/* Before/After strip — only rendered by the parent when the service
   actually has media tagged with a stage + pair_group. We never pair
   up ordinary photos ourselves. */
function BeforeAfterStrip({ pairs }) {
  const { isMobile } = useBreakpoint();
  if (!pairs.length) return null;
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
      {pairs.map((p,i) => (
        <div key={p.group||i} style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
          {["before","after"].map(stage => {
            const m = p[stage];
            return (
              <div key={stage} style={{ position:"relative", aspectRatio:"1/1", borderRadius:T.r12, overflow:"hidden", background:"#e5e7eb", border:`1px solid ${T.border}` }}>
                {m ? <img src={m.url} alt={stage} style={{width:"100%",height:"100%",objectFit:"cover"}}/> : <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",color:T.faint,fontSize:11,fontFamily:T.font}}>No {stage} photo</div>}
                <span style={{ position:"absolute", top:6, left:6, background: stage==="after"?T.green:"rgba(13,17,23,0.65)", color:"#fff", fontSize:9.5, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.05em", borderRadius:T.rFull, padding:"2px 8px", fontFamily:T.font }}>{stage}</span>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────
   SHARE POPOVER
───────────────────────────────────── */
function SharePopover({ onClose, title }) {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href : "";
  const copy = () => { navigator.clipboard?.writeText(url).then(()=>{ setCopied(true); setTimeout(()=>setCopied(false),1800); }); };
  return (
    <div onClick={e=>e.stopPropagation()} style={{ position:"absolute", top:"calc(100% + 8px)", left:0, zIndex:50, background:T.white, border:`1px solid ${T.border}`, borderRadius:T.r14, boxShadow:T.s3, padding:10, display:"flex", gap:8, width:220 }}>
      <button onClick={()=>window.open(`https://wa.me/?text=${encodeURIComponent(`Check out this JiraniApp listing: ${title} — ${url}`)}`)} style={{ flex:1, padding:"9px 0", borderRadius:T.r10, border:`1px solid ${T.border}`, background:T.white, cursor:"pointer", fontSize:11.5, fontFamily:T.font, color:T.textMid }}>WhatsApp</button>
      <button onClick={copy} style={{ flex:1, padding:"9px 0", borderRadius:T.r10, border:`1px solid ${T.border}`, background:T.white, cursor:"pointer", fontSize:11.5, fontFamily:T.font, color:T.textMid }}>{copied?"Copied!":"Copy link"}</button>
    </div>
  );
}

/* ─────────────────────────────────────
   PRODUCT CARD — used for Featured,
   "Customers also viewed", and "More
   from this pro" rows. Each links to
   its own service product page.
───────────────────────────────────── */
function ScrollRow({ children }) {
  const ref = useRef(null);
  const { isMobile } = useBreakpoint();
  const scrollBy = (dx) => ref.current?.scrollBy({ left: dx, behavior: "smooth" });
  return (
    <div style={{ position:"relative" }}>
      {!isMobile && (
        <>
          <button onClick={()=>scrollBy(-560)} style={{ position:"absolute", left:-12, top:"50%", transform:"translateY(-50%)", zIndex:2, width:30, height:30, borderRadius:"50%", background:T.white, border:`1px solid ${T.border}`, boxShadow:T.s2, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color:T.textMid }}><span style={{transform:"scale(0.8)",display:"flex"}}>{I.chevL}</span></button>
          <button onClick={()=>scrollBy(560)} style={{ position:"absolute", right:-12, top:"50%", transform:"translateY(-50%)", zIndex:2, width:30, height:30, borderRadius:"50%", background:T.white, border:`1px solid ${T.border}`, boxShadow:T.s2, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color:T.textMid }}><span style={{transform:"scale(0.8)",display:"flex"}}>{I.chevR}</span></button>
        </>
      )}
      <div ref={ref} className="jp-scroll" style={{ display:"flex", gap:10, overflowX:"auto", scrollSnapType:"x mandatory", WebkitOverflowScrolling:"touch", paddingBottom:2 }}>{children}</div>
    </div>
  );
}
function ListingCard({ item, navigate, ribbon }) {
  const [hov, setHov] = useState(false);
  return (
    <div onClick={()=>navigate(`/service/${item.id}`)} onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)} style={{
      flexShrink:0, width:142, scrollSnapAlign:"start", cursor:"pointer",
      background:T.white, border:`1px solid ${hov?"#d1d5db":T.border}`, borderRadius:T.r12, overflow:"hidden",
      boxShadow: hov ? T.s2 : "none", transition:"all 0.16s ease-out",
    }}>
      <div style={{ position:"relative", width:"100%", aspectRatio:"1/1", background:"#e5e7eb" }}>
        {item.img ? (
          <img src={item.img} alt={item.name} style={{ width:"100%", height:"100%", objectFit:"cover" }} onError={e=>{ e.currentTarget.style.display="none"; }} />
        ) : (
          <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", background: avatarColor(item.proName||"") }}>
            <span style={{ color:"#fff", fontSize:26, fontWeight:600, fontFamily:T.font }}>{toInitials(item.proName||"")}</span>
          </div>
        )}
        {(ribbon || item.badge) && (
          <span style={{ position:"absolute", top:6, left:6, background: ribbon ? T.green : T.amber, color:"#fff", fontSize:8.5, fontWeight:700, fontFamily:T.font, letterSpacing:"0.05em", textTransform:"uppercase", borderRadius:T.rFull, padding:"2px 7px" }}>{ribbon || item.badge}</span>
        )}
      </div>
      <div style={{ padding:"8px 9px 10px" }}>
        <p style={{ margin:"0 0 2px", fontSize:13.5, fontWeight:700, color:T.text, fontFamily:T.font, letterSpacing:"-0.02em" }}>{fmtKsh(item.price)}</p>
        <p style={{ margin:"0 0 3px", fontSize:11, color:T.textMid, fontFamily:T.font, lineHeight:1.4, display:"-webkit-box", WebkitLineClamp:1, WebkitBoxOrient:"vertical", overflow:"hidden" }}>{item.name}</p>
        <p style={{ margin:"0 0 3px", fontSize:10, color:T.faint, fontFamily:T.font, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{item.proName}</p>
        {item.rating != null && (
          <div style={{ display:"flex", alignItems:"center", gap:3, marginBottom:3 }}>
            <span style={{ display:"flex", transform:"scale(0.85)" }}>{I.star}</span>
            <span style={{ fontSize:10.5, fontWeight:600, color:T.text, fontFamily:T.font }}>{item.rating.toFixed(1)}</span>
            <span style={{ fontSize:10, color:T.faint, fontFamily:T.font }}>({item.reviews})</span>
          </div>
        )}
        {item.area && (
          <div style={{ display:"flex", alignItems:"center", gap:3, color:T.faint }}>
            <span style={{ display:"flex", transform:"scale(0.85)" }}>{I.pin}</span>
            <span style={{ fontSize:10, fontFamily:T.font, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{item.area}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────
   VERIFY ROW / REVIEW CARD / RATING
───────────────────────────────────── */
function VerifyRow({ label, ok }) {
  return (
    <div style={{display:"flex",alignItems:"center",gap:9}}>
      <div style={{ width:18,height:18,borderRadius:"50%",flexShrink:0, background:ok?T.greenTint:"#f3f4f6", display:"flex",alignItems:"center",justifyContent:"center", color:ok?T.green:T.faint }}>
        {ok ? <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>
            : <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>}
      </div>
      <span style={{fontSize:12.5,color:ok?T.textMid:T.faint,fontFamily:T.font,fontWeight:ok?500:400}}>{label}</span>
    </div>
  );
}
function ReviewCard({ review }) {
  const [exp,setExp] = useState(false);
  const text = review.comment || "";
  const long = text.length > 220;
  return (
    <div style={{padding:"16px 0",borderBottom:`1px solid ${T.borderFaint}`}}>
      <div style={{display:"flex",alignItems:"flex-start",gap:11,marginBottom:8}}>
        <Avatar name={review.reviewer?.full_name||"Client"} url={review.reviewer?.avatar_url} size={32}/>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:"flex",alignItems:"center",gap:7,flexWrap:"wrap",marginBottom:3}}>
            <span style={{fontSize:12.5,fontWeight:600,color:T.text,fontFamily:T.font}}>{review.reviewer?.full_name||"Client"}</span>
            <Badge variant="green">Verified booking</Badge>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <Stars n={review.rating} size={10.5}/>
            <span style={{fontSize:10.5,color:T.faint,fontFamily:T.font}}>{timeAgo(review.created_at)}</span>
          </div>
        </div>
      </div>
      <p style={{fontSize:13,color:T.textMid,lineHeight:1.75,margin:0,fontFamily:T.font}}>{exp||!long ? text : text.slice(0,220)+"..."}</p>
      {long && <button onClick={()=>setExp(o=>!o)} style={{ background:"none",border:"none",color:T.green,fontSize:12.5, fontWeight:500,cursor:"pointer",padding:"5px 0 0", fontFamily:T.font,display:"flex",alignItems:"center",gap:4 }}>{exp?"Show less":"Read more"} <span style={{display:"flex"}}>{exp?I.chevU:I.chevD}</span></button>}
    </div>
  );
}
function RatingBreakdown({ reviews, avgOverride, countOverride }) {
  const counts = [5,4,3,2,1].map(s=>({s,n:reviews.filter(r=>r.rating===s).length}));
  const max = Math.max(...counts.map(c=>c.n),1);
  const avg = avgOverride ?? (reviews.length ? (reviews.reduce((a,r)=>a+r.rating,0)/reviews.length) : null);
  return (
    <div style={{display:"flex",gap:22,alignItems:"flex-start",flexWrap:"wrap"}}>
      <div style={{textAlign:"center",flexShrink:0}}>
        <p style={{margin:0,fontSize:38,fontWeight:700,color:T.text,fontFamily:T.font,letterSpacing:"-0.07em",lineHeight:1}}>{avg?avg.toFixed(1):"N/A"}</p>
        <div style={{marginTop:5,display:"flex",justifyContent:"center"}}><Stars n={Math.round(avg||0)} size={12}/></div>
        <p style={{margin:"5px 0 0",fontSize:11,color:T.faint,fontFamily:T.font}}>{countOverride ?? reviews.length} reviews</p>
      </div>
      <div style={{flex:1,minWidth:180,display:"flex",flexDirection:"column",gap:5,paddingTop:4}}>
        {counts.map(c=>(
          <div key={c.s} style={{display:"flex",alignItems:"center",gap:7}}>
            <span style={{fontSize:11,color:T.faint,fontFamily:T.font,width:8,textAlign:"right",flexShrink:0}}>{c.s}</span>
            <span style={{display:"flex",flexShrink:0,transform:"scale(0.85)"}}>{I.star}</span>
            <div style={{flex:1,height:4,borderRadius:T.rFull,background:"#ede9e4",overflow:"hidden"}}><div style={{height:"100%",width:`${Math.round((c.n/max)*100)}%`,background:"#f59e0b",borderRadius:T.rFull,transition:"width 0.4s ease"}}/></div>
            <span style={{fontSize:10.5,color:T.faint,fontFamily:T.font,width:14,flexShrink:0}}>{c.n}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Trust Score card — shows the number AND its real inputs so it never
   reads as a made-up figure. */
function TrustScoreCard({ pro }) {
  const [open, setOpen] = useState(false);
  const result = computeTrustScore(pro);
  if (!result) return null;
  return (
    <div style={{ background:T.white, border:`1.5px solid ${T.border}`, borderRadius:T.r16, padding:16, boxShadow:T.s1 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom: open ? 12 : 0 }}>
        <div>
          <p style={{ margin:"0 0 2px", fontSize:11, fontWeight:700, color:T.text, fontFamily:T.font, textTransform:"uppercase", letterSpacing:"0.06em" }}>Jirani Trust Score</p>
          <p style={{ margin:0, fontSize:26, fontWeight:700, color:T.green, fontFamily:T.font, letterSpacing:"-0.04em" }}>{result.score}<span style={{fontSize:14,color:T.faint,fontWeight:500}}>/100</span></p>
        </div>
        <button onClick={()=>setOpen(o=>!o)} style={{ background:"none", border:"none", cursor:"pointer", color:T.sub, fontSize:11.5, fontFamily:T.font, display:"flex", alignItems:"center", gap:3 }}>
          How it's calculated {open ? I.chevU : I.chevD}
        </button>
      </div>
      {open && (
        <div style={{ display:"flex", flexDirection:"column", gap:6, paddingTop:4 }}>
          {result.inputs.map(p => (
            <div key={p.label} style={{ display:"flex", justifyContent:"space-between", fontSize:11.5, fontFamily:T.font }}>
              <span style={{ color:T.sub }}>{p.label}</span>
              <span style={{ color:T.textMid, fontWeight:600 }}>{Math.round(p.value)}/100</span>
            </div>
          ))}
          <p style={{ margin:"4px 0 0", fontSize:10.5, color:T.faint, fontFamily:T.font, lineHeight:1.5 }}>Weighted from this pro's real rating, completed jobs, repeat clients, response rate, and verification status.</p>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────
   BOOKING MODAL — same job_requests
   insert shape as ProProfilePage's
   BookingModal. Price now depends on
   the selected package if the service
   has tiers, otherwise the base price.
───────────────────────────────────── */
const labelSx = { margin:"0 0 7px",fontSize:11,fontWeight:600,color:T.textMid, fontFamily:T.font,textTransform:"uppercase",letterSpacing:"0.07em", display:"block" };
function PriceRow({ label, val, faint:isFaint, bold }) {
  return <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:isFaint?5:0}}><span style={{fontSize:isFaint?12:13,color:isFaint?T.faint:T.sub,fontFamily:T.font}}>{label}</span><span style={{fontSize:bold?14:13,fontWeight:bold?700:500,color:bold?T.text:T.textMid,fontFamily:T.font}}>{val}</span></div>;
}
function ErrorBox({ msg }) { return <div style={{background:T.redTint,border:`1px solid ${T.redBd}`,borderRadius:T.r10,padding:"12px 14px",marginBottom:16}}><p style={{margin:0,fontSize:13,color:T.red,fontFamily:T.font}}>{msg}</p></div>; }

/* M-Pesa is the only payment method wired to the backend today
   (mpesa-stk-push edge function). We don't advertise Airtel Money,
   Visa, or Mastercard anywhere on this page until they're actually
   implemented — showing them would be a trust-breaking bug, not a
   trust signal. */
function MpesaBlock({ bookingId, amount, userPhone, onSuccess }) {
  const [phone, setPhone] = useState(userPhone || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);

  const pay = async () => {
    if (!phone.trim()) { setError("Enter your M-Pesa number."); return; }
    setError(""); setLoading(true);
    try {
      const { error: fnErr } = await supabase.functions.invoke("mpesa-stk-push", { body: { booking_id: bookingId, phone } });
      if (fnErr) throw fnErr;
      await supabase.from("job_requests").update({ payment_status: "paid" }).eq("id", bookingId);
      onSuccess();
    } catch (err) {
      setError(err.message || "Payment failed. Please try again.");
    } finally { setLoading(false); }
  };

  if (!open) return <Btn v="primary" size="lg" full onClick={()=>setOpen(true)}><span style={{display:"flex"}}>{I.mpesa}</span> Pay {fmtKsh(amount)} with M-Pesa</Btn>;

  return (
    <div style={{ border:`1.5px solid ${T.greenBd}`, background:T.greenTint, borderRadius:T.r14, padding:16 }}>
      <p style={labelSx}>M-Pesa phone number</p>
      <input value={phone} onChange={e=>setPhone(e.target.value)} placeholder="+254 712 000 000" style={{ width:"100%", padding:"11px 14px", borderRadius:T.r10, border:`1.5px solid ${T.border}`, fontSize:14, fontFamily:T.font, outline:"none", boxSizing:"border-box", marginBottom:10 }} />
      {error && <ErrorBox msg={error}/>}
      <div style={{ display:"flex", gap:8 }}>
        <Btn v="ghost" size="md" full onClick={()=>setOpen(false)} disabled={loading}>Cancel</Btn>
        <Btn v="primary" size="md" full onClick={pay} disabled={loading}>{loading?"Sending STK…":`Pay ${fmtKsh(amount)}`}</Btn>
      </div>
    </div>
  );
}

function BookingModal({ service, price, pro, onClose, onDone, user, navigate }) {
  const { open: openLogin } = useLoginModal();
  const { isMobile } = useBreakpoint();
  const [step, setStep] = useState(user ? 0 : "auth");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("09:00");
  const [addr, setAddr] = useState(user?.address || "");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [booking, setBooking] = useState(null);
  const [paid, setPaid] = useState(false);

  const basePrice = price ?? service.price ?? 0;
  const fee = Math.round(basePrice*0.05);
  const total = basePrice + fee;
  const todayStr = new Date().toISOString().split("T")[0];
  const TIMES = ["07:00","08:00","09:00","10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00","18:00"];

  useEffect(() => { if (user && step === "auth") setStep(0); }, [user, step]);

  const submit = async () => {
    if (!date) { setError("Please choose a date."); return; }
    if (!addr.trim()) { setError("Please enter your address."); return; }
    if (!user?.id) { setError("Please log in to continue."); return; }
    setError(""); setBusy(true);
    try {
      const scheduled = new Date(`${date}T${time}`).toISOString();
      const { data: row, error: bErr } = await supabase.from("job_requests").insert({
        user_id: user.id, pro_id: pro.id, service_id: service.id,
        description: service.name, status: "pending", scheduled_time: scheduled,
        location: addr, notes, estimated_price: basePrice, payment_status: "unpaid",
      }).select().single();
      if (bErr) throw bErr;

      try {
        await supabase.from("notifications").insert({
          user_id: pro.id, type: "new_booking", title: "New booking request",
          body: `${user.full_name||"A client"} requested ${service.name} on ${fmtShortDate(scheduled)}.`,
          metadata: { booking_id: row.id }, read: false,
        });
      } catch { /* best-effort */ }

      setBooking(row);
      setStep(2);
    } catch (e) {
      setError(e.message || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  const inputStyle = { width:"100%",padding:"11px 14px",borderRadius:T.r10, border:`1.5px solid ${T.border}`,fontSize:14,outline:"none", fontFamily:T.font,color:T.text,background:T.white, boxSizing:"border-box",minHeight:44 };

  return (
    <>
      <div onClick={onClose} style={{position:"fixed",inset:0,zIndex:900,background:"rgba(13,17,23,0.52)",backdropFilter:"blur(4px)"}}/>
      <div style={{ position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)", zIndex:901, width: isMobile ? "calc(100vw - 32px)" : 480, maxHeight:"92vh",overflowY:"auto", background:T.white,borderRadius:T.r20,boxShadow:T.s4 }}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"20px 24px 16px",borderBottom:`1px solid ${T.borderFaint}`}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <Avatar name={pro.profiles?.full_name} url={pro.profiles?.avatar_url} size={36}/>
            <div>
              <p style={{margin:0,fontSize:15,fontWeight:600,color:T.text,fontFamily:T.font}}>Book {(pro.profiles?.full_name||"Pro").split(" ")[0]}</p>
              <p style={{margin:"1px 0 0",fontSize:12,color:T.faint,fontFamily:T.font}}>{service.name} &middot; {fmtKsh(basePrice)}</p>
            </div>
          </div>
          <button onClick={onClose} style={{ width:32,height:32,borderRadius:"50%",background:"#f3f4f6", border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.sub }}>{I.close}</button>
        </div>

        {step === "auth" && (
          <div style={{padding:"32px 24px",textAlign:"center"}}>
            <div style={{ width:52,height:52,borderRadius:"50%",background:T.greenTint, display:"flex",alignItems:"center",justifyContent:"center", margin:"0 auto 16px",color:T.green }}>{I.user}</div>
            <p style={{margin:"0 0 8px",fontSize:17,fontWeight:600,color:T.text,fontFamily:T.font}}>Sign in to continue</p>
            <p style={{margin:"0 0 24px",fontSize:14,color:T.sub,fontFamily:T.font,lineHeight:1.6}}>Create a free account or sign in to book this service securely.</p>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <Btn v="primary" size="lg" full onClick={()=>openLogin({redirect:window.location.pathname})}>Sign in</Btn>
              <Btn v="ghost" size="lg" full onClick={()=>navigate("/signup",{state:{redirect:window.location.pathname}})}>Create a free account</Btn>
            </div>
          </div>
        )}

        {step === 0 && (
          <div style={{padding:"20px 24px 24px"}}>
            <div style={{marginBottom:16}}>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:7}}><span style={{color:T.faint,display:"flex"}}>{I.cal}</span><p style={labelSx}>Date</p></div>
              <input type="date" value={date} min={todayStr} onChange={e=>setDate(e.target.value)} style={inputStyle}/>
            </div>
            <div style={{marginBottom:16}}>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:7}}><span style={{color:T.faint,display:"flex"}}>{I.clock}</span><p style={labelSx}>Time</p></div>
              <select value={time} onChange={e=>setTime(e.target.value)} style={inputStyle}>{TIMES.map(t=><option key={t} value={t}>{fmtClock(t)}</option>)}</select>
            </div>
            <div style={{marginBottom:16}}>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:7}}><span style={{color:T.faint,display:"flex"}}>{I.pin}</span><p style={labelSx}>Your address <span style={{color:T.red}}>*</span></p></div>
              <input type="text" value={addr} onChange={e=>setAddr(e.target.value)} placeholder="e.g. Westlands, Nairobi" style={inputStyle}/>
            </div>
            <div style={{marginBottom:22}}>
              <p style={labelSx}>Notes (optional)</p>
              <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={3} placeholder="Gate code, access instructions, or anything the pro should know…" style={{...inputStyle,resize:"vertical",minHeight:80,padding:"11px 14px"}}/>
            </div>
            {error && <ErrorBox msg={error}/>}
            <Btn v="primary" size="lg" full onClick={()=>{ if (!date){setError("Please choose a date.");return;} if(!addr.trim()){setError("Please enter your address.");return;} setError(""); setStep(1); }}>Review booking</Btn>
          </div>
        )}

        {step === 1 && (
          <div style={{padding:"20px 24px 24px"}}>
            <p style={{margin:"0 0 18px",fontSize:14,fontWeight:600,color:T.text,fontFamily:T.font}}>Review your booking</p>
            <div style={{background:"#fafafa",borderRadius:T.r12,padding:"16px 18px",marginBottom:20,border:`1px solid ${T.borderFaint}`}}>
              {[{label:"Service",value:service.name},{label:"Date",value:fmtDate(date)},{label:"Time",value:fmtClock(time)},{label:"Address",value:addr}, ...(notes?[{label:"Notes",value:notes}]:[])].map(r=>(
                <div key={r.label} style={{display:"flex",gap:10,marginBottom:10,alignItems:"flex-start"}}>
                  <span style={{fontSize:11,fontWeight:600,color:T.faint,fontFamily:T.font,textTransform:"uppercase",letterSpacing:"0.07em",minWidth:60,flexShrink:0,paddingTop:1}}>{r.label}</span>
                  <span style={{fontSize:13,fontWeight:500,color:T.textMid,fontFamily:T.font,lineHeight:1.5}}>{r.value}</span>
                </div>
              ))}
            </div>
            <div style={{background:"#fafafa",borderRadius:T.r12,padding:"14px 18px",marginBottom:20,border:`1px solid ${T.borderFaint}`}}>
              <PriceRow label={service.name} val={fmtKsh(basePrice)}/>
              <PriceRow label="Platform fee (5%)" val={fmtKsh(fee)} faint/>
              <Div my={10}/>
              <PriceRow label="Total" val={fmtKsh(total)} bold/>
            </div>
            <div style={{ background:T.amberBg,border:`1px solid ${T.amberBd}`, borderRadius:T.r10,padding:"11px 14px",marginBottom:20, display:"flex",gap:9,alignItems:"flex-start" }}>
              <span style={{color:T.amber,flexShrink:0,display:"flex",marginTop:1}}>{I.info}</span>
              <p style={{margin:0,fontSize:12,color:T.amber,fontFamily:T.font,lineHeight:1.65}}><strong>No payment now.</strong> You'll get an M-Pesa prompt after the pro confirms — nothing is charged until you approve it.</p>
            </div>
            {error && <ErrorBox msg={error}/>}
            <Btn v="primary" size="lg" full onClick={submit} disabled={busy}>{busy?"Confirming…":`Confirm booking · ${fmtKsh(total)}`}</Btn>
            <button onClick={()=>setStep(0)} style={{ display:"block",margin:"12px auto 0",background:"none",border:"none", fontSize:13,color:T.faint,cursor:"pointer",fontFamily:T.font }}>Edit details</button>
          </div>
        )}

        {step === 2 && booking && (
          <div style={{padding:"24px"}}>
            <div style={{ textAlign:"center", marginBottom:20 }}>
              <div style={{ width:52,height:52,borderRadius:"50%", background:`linear-gradient(135deg,${T.green},#2ab86d)`, display:"flex",alignItems:"center",justifyContent:"center", margin:"0 auto 12px", color:"#fff", boxShadow:T.sGreen }}>{I.checkFat}</div>
              <p style={{ margin:"0 0 4px", fontSize:17, fontWeight:700, color:T.greenDark, fontFamily:T.font }}>{paid ? "Payment confirmed!" : "Booking request sent"}</p>
              <p style={{ margin:0, fontSize:13, color:T.sub, fontFamily:T.font }}>
                {paid ? "The pro will start working on this right away." : `${(pro.profiles?.full_name||"The pro").split(" ")[0]} has been notified and will confirm within the hour.`}
              </p>
            </div>

            {!paid && <div style={{ marginBottom:16 }}><MpesaBlock bookingId={booking.id} amount={total} userPhone={user?.phone} onSuccess={()=>setPaid(true)} /></div>}

            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              <Btn v="primary" size="lg" full onClick={()=>{ onDone(); navigate("/bookings"); }}>View my bookings</Btn>
              <Btn v="ghost" size="md" full onClick={onDone}>Done</Btn>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

/* ─────────────────────────────────────
   PAGE ENTRY
───────────────────────────────────── */
export default function ServiceProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { open: openLogin } = useLoginModal();
  const { user } = useAuth();
  const { isMobile } = useBreakpoint();

  const [loading, setLoading] = useState(true);
  const [service, setService] = useState(null);
  const [pro, setPro] = useState(null);
  const [media, setMedia] = useState([]);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [nextSlot, setNextSlot] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [moreFromPro, setMoreFromPro] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [alsoViewed, setAlsoViewed] = useState([]);
  const [saved, setSaved] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (!id || id === "demo") {
        setService(DEMO_SERVICE); setPro(DEMO_SERVICE.pro); setMedia(DEMO_MEDIA); setReviews(DEMO_REVIEWS);
        setMoreFromPro([]); setFeatured([]); setAlsoViewed([]); setPackages([]); setNextSlot(null);
        return;
      }
      const row = await fetchService(id);
      const proData = row.pro;
      setService(row);
      setPro(proData);

      const [mediaRows, revRows, pkgRows, slot] = await Promise.all([
        fetchServiceMedia(id).catch(()=>[]),
        fetchProReviews(proData.id).catch(()=>[]),
        fetchServicePackages(id).catch(()=>[]),
        fetchNextAvailableSlot(proData.id).catch(()=>null),
      ]);
      setMedia(mediaRows);
      setReviews(revRows);
      setPackages(pkgRows);
      setNextSlot(slot);

      const [more, feat, also] = await Promise.all([
        fetchListings({ matchProId: proData.id, excludeServiceId: id, limit: 12 }).catch(()=>[]),
        fetchListings({ category: row.category, excludeProId: proData.id, excludeServiceId: id, limit: 18 }).catch(()=>[]),
        fetchListings({ category: row.category, excludeServiceId: id, limit: 18 }).catch(()=>[]),
      ]);
      setMoreFromPro(more);
      setFeatured(feat);
      setAlsoViewed(also);

      if (user?.id) {
        try {
          const { data: fav } = await supabase.from("favorites").select("id").eq("customer_id", user.id).eq("professional_id", proData.id).maybeSingle();
          setSaved(!!fav);
        } catch {}
      }
    } catch (e) {
      console.error("Service page load failed:", e);
      setService(null);
    } finally {
      setLoading(false);
    }
  }, [id, user?.id]);

  useEffect(() => { load(); window.scrollTo(0,0); }, [load]);

  const handleToggleSave = useCallback(async () => {
    if (!user?.id || !pro) { setShowModal(true); return; }
    if (saved) { await supabase.from("favorites").delete().eq("customer_id",user.id).eq("professional_id",pro.id); setSaved(false); }
    else { await supabase.from("favorites").insert({customer_id:user.id,professional_id:pro.id}); setSaved(true); }
  }, [saved, user?.id, pro]);

  const handleMessage = useCallback(async () => {
    if (!user?.id || !pro) { setShowModal(true); return; }
    try {
      const { data: ex } = await supabase.from("conversations").select("id").contains("participant_ids",[user.id, pro.id]).maybeSingle();
      if (ex?.id) { navigate(`/messages/${ex.id}`); return; }
      const { data: nc, error } = await supabase.from("conversations").insert({ customer_id:user.id, professional_id:pro.id, participant_ids:[user.id,pro.id], updated_at:new Date().toISOString() }).select("id").single();
      if (error) throw error;
      await supabase.from("conversation_participants").insert([{conversation_id:nc.id,user_id:user.id},{conversation_id:nc.id,user_id:pro.id}]);
      navigate(`/messages/${nc.id}`);
    } catch { navigate("/messages"); }
  }, [user?.id, pro, navigate]);

  // Before/after pairs — only if the pro actually tagged media this way.
  // IMPORTANT: this hook must run on every render in the same order, so
  // it lives here, above the loading/!service early returns below —
  // never after a conditional return. `media` defaults to [] before
  // the fetch resolves, so this is safe to compute unconditionally.
  const beforeAfterPairs = useMemo(() => {
    const groups = {};
    media.forEach(m => {
      if (!m.stage || !m.pair_group) return;
      groups[m.pair_group] = groups[m.pair_group] || { group: m.pair_group };
      groups[m.pair_group][m.stage] = m;
    });
    return Object.values(groups).filter(g => g.before && g.after);
  }, [media]);

  if (loading) return <div style={{minHeight:"100vh",background:T.bg,fontFamily:T.font}}><style>{CSS}</style><PageSkeleton/></div>;
  if (!service || !pro) return <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:T.font}}><p style={{color:T.faint}}>This listing isn't available.</p></div>;

  const proName = pro.profiles?.full_name || pro.jirani_name || "JiraniPro";
  const durationLabel = fmtDuration(service.duration_minutes);
  const relatedSearches = buildRelatedSearches(service.category, (pro.profiles?.location||"Nairobi").split(",")[0]);
  const hasPackages = packages.length > 0;
  const activePrice = hasPackages ? (selectedPackage?.price ?? packages[0].price) : service.price;

  // Real quick-facts strip above the fold — every line here is backed
  // by an actual field. Nothing is invented to fill space.
  const quickFacts = [
    durationLabel ? `${durationLabel} session` : null,
    service.team_size ? `${service.team_size} professional${service.team_size>1?"s":""}` : null,
    service.supplies_included ? "Supplies included" : null,
    pro.review_count ? `Trusted by ${pro.review_count} customers` : null,
    pro.availability === "available" && (pro.profiles?.location) ? `Available in ${pro.profiles.location.split(",")[0]} today` : null,
  ].filter(Boolean);

  return (
    <div style={{minHeight:"100vh",background:T.bg,fontFamily:T.font}}>
      <style>{CSS}</style>

      <div style={{maxWidth:1120,margin:"0 auto",padding: isMobile ? "12px 14px 0" : "16px 20px 0"}}>
        <button onClick={()=>navigate(-1)} style={{ display:"inline-flex",alignItems:"center",gap:6,marginBottom:10, background:"none",border:"none",cursor:"pointer",padding:0, fontSize:12.5,color:T.sub,fontFamily:T.font }}>
          {I.back} Back to search
        </button>
        <p style={{ margin:"0 0 14px", fontSize:11.5, color:T.faint, fontFamily:T.font }}>
          <span onClick={()=>navigate("/")} style={{cursor:"pointer"}}>Home</span> {" › "}
          <span onClick={()=>navigate(`/search?category=${service.category}`)} style={{cursor:"pointer"}}>{categoryLabel(service.category)}</span> {" › "}
          <span style={{color:T.textMid,fontWeight:500}}>{service.name}</span>
        </p>
      </div>

      {/* HERO — gallery + identity + price. Grid column for the gallery
          is now wide enough (480px) for the 440px image, so thumbnails
          never overlap the title/price column next to it. */}
      <div style={{maxWidth:1120,margin:"0 auto",padding: isMobile ? "0 14px 16px" : "0 20px 20px"}}>
        <div style={{display:"grid",gridTemplateColumns: isMobile ? "1fr" : "minmax(0,480px) 1fr 300px",gap:20,alignItems:"start"}}>

          <ProductGallery media={media} name={service.name} />

          <div style={{minWidth:0}}>
            <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:6}}>
              {pro.verified && <Badge variant="blue"><span style={{display:"flex",alignItems:"center",gap:3}}>{I.verified} Verified pro</span></Badge>}
              {pro.badge && <Badge variant="amber">{pro.badge}</Badge>}
            </div>
            {/* Headline — falls back to the plain service name if the
                pro hasn't written a headline yet. No copy is invented. */}
            <h1 style={{margin:"0 0 6px",fontFamily:T.serif,fontStyle:"italic",fontWeight:400,fontSize:isMobile?22:27,color:T.text,letterSpacing:"-0.02em",lineHeight:1.2}}>{service.headline || service.name}</h1>
            <div onClick={()=>navigate(`/pro/${pro.id}`)} style={{ display:"inline-flex", alignItems:"center", gap:8, cursor:"pointer", marginBottom:10 }}>
              <Avatar name={proName} url={pro.profiles?.avatar_url} size={26}/>
              <span style={{ fontSize:13, fontWeight:600, color:T.textMid, fontFamily:T.font }}>{proName}</span>
              <span style={{ fontSize:12, color:T.green, fontFamily:T.font, fontWeight:500 }}>View profile</span>
            </div>

            <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:12}}>
              <a href="#sec-reviews" style={{ display:"flex", alignItems:"center", gap:5, textDecoration:"none" }}>
                <Stars n={pro.avg_rating||0} size={13}/>
                <span style={{fontSize:13,fontWeight:600,color:T.text,fontFamily:T.font}}>{(pro.avg_rating||0).toFixed(1)}</span>
                <span style={{fontSize:12.5,color:T.faint,fontFamily:T.font}}>({pro.review_count ?? reviews.length} verified ratings)</span>
              </a>
              <span style={{color:T.border}}>|</span>
              <div style={{display:"flex",alignItems:"center",gap:5}}>
                <span style={{width:6,height:6,borderRadius:"50%",background:pro.availability==="available"?T.green:T.faint,flexShrink:0}}/>
                <span style={{fontSize:12.5,fontWeight:500,color:pro.availability==="available"?T.green:T.faint,fontFamily:T.font}}>
                  {nextSlot ? `Next slot: ${nextSlot.label} at ${nextSlot.time.toLocaleTimeString("en-KE",{hour:"numeric",minute:"2-digit"})}` : (pro.availability==="available"?"Available now":"Currently busy")}
                </span>
              </div>
            </div>

            {/* Quick facts checklist — real data only */}
            {quickFacts.length > 0 && (
              <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:14 }}>
                {quickFacts.map((f,i) => (
                  <div key={i} style={{ display:"flex", alignItems:"center", gap:7 }}>
                    <span style={{ color:T.green, display:"flex", flexShrink:0 }}>{I.check}</span>
                    <span style={{ fontSize:13, color:T.textMid, fontFamily:T.font }}>{f}</span>
                  </div>
                ))}
              </div>
            )}

            {!hasPackages && (
              <>
                <div style={{ display:"flex", alignItems:"baseline", gap:8, marginBottom:4 }}>
                  <span style={{fontSize:30,fontWeight:700,color:T.text,fontFamily:T.font,letterSpacing:"-0.05em"}}>{fmtKsh(service.price)}</span>
                  {durationLabel && <span style={{fontSize:13,color:T.faint,fontFamily:T.font}}>/ {durationLabel}</span>}
                </div>
                <p style={{margin:"0 0 16px",fontSize:12,color:T.faint,fontFamily:T.font}}>Price per booking · platform fee added at checkout</p>
              </>
            )}

            {/* Package tiers — only rendered if the pro has actually set
                any up in service_packages. Otherwise this whole block
                is absent and the single price above is used. */}
            {hasPackages && (
              <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:16 }}>
                {packages.map((pkg, i) => {
                  const isSel = (selectedPackage?.id ?? packages[0].id) === pkg.id;
                  return (
                    <button key={pkg.id} onClick={()=>setSelectedPackage(pkg)} style={{
                      textAlign:"left", display:"flex", justifyContent:"space-between", alignItems:"center",
                      padding:"12px 14px", borderRadius:T.r12, cursor:"pointer",
                      border: isSel ? `2px solid ${T.green}` : `1.5px solid ${T.border}`,
                      background: isSel ? T.greenTint : T.white,
                    }}>
                      <div>
                        <div style={{display:"flex",alignItems:"center",gap:6}}>
                          <span style={{fontSize:13.5,fontWeight:700,color:T.text,fontFamily:T.font}}>{pkg.name}</span>
                          {i===1 && packages.length>=3 && <Badge variant="amber">Most popular</Badge>}
                        </div>
                        {pkg.description && <p style={{margin:"2px 0 0",fontSize:11.5,color:T.faint,fontFamily:T.font}}>{pkg.description}</p>}
                      </div>
                      <span style={{fontSize:15,fontWeight:700,color:T.text,fontFamily:T.font,flexShrink:0,marginLeft:12}}>{fmtKsh(pkg.price)}</span>
                    </button>
                  );
                })}
                <p style={{margin:"2px 0 0",fontSize:12,color:T.faint,fontFamily:T.font}}>Platform fee added at checkout</p>
              </div>
            )}

            {pro.response_minutes && (
              <div style={{ display:"inline-flex", alignItems:"center", gap:6, background:T.greenTint, border:`1px solid ${T.greenBd}`, borderRadius:T.r10, padding:"7px 12px", marginBottom:18 }}>
                <span style={{color:T.green,display:"flex"}}>{I.clock}</span>
                <span style={{fontSize:12,color:T.greenDark,fontFamily:T.font,fontWeight:500}}>Usually responds within {pro.response_minutes} min</span>
              </div>
            )}

            <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:14}}>
              <Btn v="primary" size="lg" onClick={()=>setShowModal(true)}>Book now</Btn>
              <Btn v="ghost" size="lg" onClick={handleMessage}><span style={{display:"flex"}}>{I.msg}</span> Message</Btn>
              <button onClick={handleToggleSave} title={saved?"Remove from saved":"Save"} style={{ width:48,height:48,borderRadius:T.r12,flexShrink:0, border:`1px solid ${T.border}`,background:T.white, cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center", color:T.sub }}>{I.heart(saved)}</button>
              <div style={{ position:"relative" }}>
                <button onClick={()=>setShowShare(o=>!o)} title="Share" style={{ width:48,height:48,borderRadius:T.r12, border:`1px solid ${T.border}`,background:T.white, cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center", color:T.sub }}>{I.share}</button>
                {showShare && <SharePopover onClose={()=>setShowShare(false)} title={service.name} />}
              </div>
            </div>

            <button onClick={()=>navigate(`/report?type=service&id=${service.id}`)} style={{ background:"none",border:"none",cursor:"pointer",padding:0, fontSize:11.5,color:T.faint,fontFamily:T.font, display:"inline-flex",alignItems:"center",gap:5 }}>
              <span style={{display:"flex"}}>{I.flag}</span> Report incorrect listing information
            </button>
          </div>

          {/* RIGHT RAIL — booking & protection + seller card + trust score + performance */}
          {!isMobile && (
            <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
              <div style={{ background:T.white, border:`1.5px solid ${T.border}`, borderRadius:T.r16, padding:16, boxShadow:T.s1 }}>
                <p style={{ margin:"0 0 10px", fontSize:11, fontWeight:700, color:T.text, fontFamily:T.font, textTransform:"uppercase", letterSpacing:"0.06em" }}>Booking & protection</p>
                {[
                  { icon:I.shield, t:"The JiraniApp Guarantee", s:"If something goes wrong, we make it right." },
                  { icon:I.mpesa,  t:"Pay securely with M-Pesa", s:"Held safely, released once the job is confirmed done. The only payment method we support today." },
                  { icon:I.cal,    t:"Free cancellation", s:"Up to 24 hrs before your scheduled time." },
                ].map((r,i)=>(
                  <div key={i} style={{ display:"flex", gap:10, alignItems:"flex-start", marginBottom: i<2?12:0 }}>
                    <div style={{ width:26,height:26,borderRadius:T.r8,background:T.greenTint,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,color:T.green }}>{r.icon}</div>
                    <div>
                      <p style={{ margin:0, fontSize:12, fontWeight:600, color:T.text, fontFamily:T.font }}>{r.t}</p>
                      <p style={{ margin:"1px 0 0", fontSize:11, color:T.faint, fontFamily:T.font, lineHeight:1.5 }}>{r.s}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ background:T.white, border:`1.5px solid ${T.border}`, borderRadius:T.r16, padding:16, boxShadow:T.s1 }}>
                <p style={{ margin:"0 0 10px", fontSize:11, fontWeight:700, color:T.text, fontFamily:T.font, textTransform:"uppercase", letterSpacing:"0.06em" }}>About this pro</p>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                  <Avatar name={proName} url={pro.profiles?.avatar_url} size={40} ring/>
                  <div style={{minWidth:0}}>
                    <p style={{ margin:0, fontSize:13.5, fontWeight:700, color:T.text, fontFamily:T.font, overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{proName}</p>
                    {pro.member_since && <p style={{ margin:"1px 0 0", fontSize:11, color:T.faint, fontFamily:T.font }}>Member since {pro.member_since}</p>}
                  </div>
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:7, marginBottom:12 }}>
                  <VerifyRow label="Identity verified" ok={!!pro.verified}/>
                  <VerifyRow label="Background checked" ok={!!pro.background_checked}/>
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <Btn v="outline" size="sm" full onClick={()=>navigate(`/pro/${pro.id}`)}>View full profile</Btn>
                </div>
              </div>

              <TrustScoreCard pro={pro} />

              <div style={{ background:T.white, border:`1.5px solid ${T.border}`, borderRadius:T.r16, padding:16, boxShadow:T.s1 }}>
                <p style={{ margin:"0 0 10px", fontSize:11, fontWeight:700, color:T.text, fontFamily:T.font, textTransform:"uppercase", letterSpacing:"0.06em" }}>Why customers choose {proName.split(" ")[0]}</p>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                  {[
                    { label:"Response", value: pro.response_minutes?`<${pro.response_minutes}m`:"—" },
                    { label:"Reply rate", value: pro.response_rate?`${pro.response_rate}%`:"—" },
                    { label:"Jobs done", value: (pro.completed_jobs||0).toLocaleString() },
                    { label:"Repeat clients", value: pro.repeat_rate?`${pro.repeat_rate}%`:"—" },
                  ].map(st=>(
                    <div key={st.label} style={{background:"#fafafa",border:`1px solid ${T.borderFaint}`,borderRadius:T.r10,padding:"9px 10px"}}>
                      <p style={{margin:"0 0 2px",fontSize:9,color:T.faint,fontFamily:T.font,textTransform:"uppercase",letterSpacing:"0.05em"}}>{st.label}</p>
                      <p style={{margin:0,fontSize:13,fontWeight:700,color:T.text,fontFamily:T.font}}>{st.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* FEATURED — merchandising row (not paid placement; just other approved pros in the same category) */}
      {featured.length > 0 && (
        <div style={{maxWidth:1120,margin:"0 auto",padding: isMobile ? "0 14px 4px" : "0 20px 4px"}}>
          <EditorialHeading eyebrow="Featured on JiraniApp" title="Other pros customers are booking" sub={`Popular ${categoryLabel(service.category).toLowerCase()} listings near you`} />
          <ScrollRow>{featured.map(item => <ListingCard key={item.id} item={item} navigate={navigate} ribbon="Featured" />)}</ScrollRow>
        </div>
      )}

      {/* BODY */}
      <div style={{maxWidth:1120,margin:"0 auto",padding: isMobile ? "18px 0 32px" : "18px 20px 32px"}}>
        <div style={{display:"grid",gridTemplateColumns: isMobile ? "1fr" : "1fr 300px",gap:18,alignItems:"start"}}>
          <div>
            <Section id="details" eyebrow="What you're booking" title="Service details">
              <p style={{margin:"0 0 4px",fontSize:13.5,color:T.textMid,lineHeight:1.8,fontFamily:T.font}}>
                {descExpanded || (service.description||"").length <= 320 ? service.description : `${(service.description||"").slice(0,320)}…`}
              </p>
              {(service.description||"").length > 320 && (
                <button onClick={()=>setDescExpanded(o=>!o)} style={{ background:"none",border:"none",color:T.green,fontSize:12.5, fontWeight:500,cursor:"pointer",padding:0,fontFamily:T.font, display:"inline-flex",alignItems:"center",gap:4 }}>
                  {descExpanded?"Show less":"Read more"} <span style={{display:"flex"}}>{descExpanded?I.chevU:I.chevD}</span>
                </button>
              )}
              <div style={{ display:"flex", flexWrap:"wrap", gap:14, marginTop:14, paddingTop:14, borderTop:`1px solid ${T.borderFaint}` }}>
                <div style={{ display:"flex", alignItems:"center", gap:6, color:T.sub }}><span style={{display:"flex"}}>{I.brief}</span><span style={{fontSize:12.5,fontFamily:T.font}}>{categoryLabel(service.category)}</span></div>
                {durationLabel && <div style={{ display:"flex", alignItems:"center", gap:6, color:T.sub }}><span style={{display:"flex"}}>{I.clock}</span><span style={{fontSize:12.5,fontFamily:T.font}}>{durationLabel} typical</span></div>}
                {service.team_size && <div style={{ display:"flex", alignItems:"center", gap:6, color:T.sub }}><span style={{display:"flex"}}>{I.user}</span><span style={{fontSize:12.5,fontFamily:T.font}}>{service.team_size} professional{service.team_size>1?"s":""}</span></div>}
                {service.supplies_included != null && <div style={{ display:"flex", alignItems:"center", gap:6, color:T.sub }}><span style={{display:"flex"}}>{I.check}</span><span style={{fontSize:12.5,fontFamily:T.font}}>{service.supplies_included ? "Supplies included" : "Bring your own supplies"}</span></div>}
                {pro.profiles?.location && <div style={{ display:"flex", alignItems:"center", gap:6, color:T.sub }}><span style={{display:"flex"}}>{I.pin}</span><span style={{fontSize:12.5,fontFamily:T.font}}>{pro.profiles.location}</span></div>}
              </div>
            </Section>

            {/* What's included / not included — only shown when the pro
                has actually filled these in. */}
            {(service.included_items?.length > 0 || service.excluded_items?.length > 0) && (
              <Section id="included" eyebrow="Scope of work" title="What's included">
                <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:20 }}>
                  {service.included_items?.length > 0 && (
                    <div>
                      <p style={{margin:"0 0 8px",fontSize:11,fontWeight:700,color:T.green,fontFamily:T.font,textTransform:"uppercase",letterSpacing:"0.06em"}}>Included</p>
                      <div style={{ display:"flex", flexDirection:"column", gap:7 }}>
                        {service.included_items.map((it,i)=>(
                          <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:7 }}>
                            <span style={{ color:T.green, display:"flex", flexShrink:0, marginTop:2 }}>{I.check}</span>
                            <span style={{ fontSize:13, color:T.textMid, fontFamily:T.font, lineHeight:1.5 }}>{it}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {service.excluded_items?.length > 0 && (
                    <div>
                      <p style={{margin:"0 0 8px",fontSize:11,fontWeight:700,color:T.faint,fontFamily:T.font,textTransform:"uppercase",letterSpacing:"0.06em"}}>Not included</p>
                      <div style={{ display:"flex", flexDirection:"column", gap:7 }}>
                        {service.excluded_items.map((it,i)=>(
                          <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:7 }}>
                            <span style={{ color:T.faint, display:"flex", flexShrink:0, marginTop:2 }}>{I.x}</span>
                            <span style={{ fontSize:13, color:T.faint, fontFamily:T.font, lineHeight:1.5 }}>{it}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Section>
            )}

            {/* Preparation notes — only if the pro wrote any. */}
            {service.prep_notes && (
              <Section id="prep" eyebrow="Before the pro arrives" title="How to prepare">
                <p style={{margin:0,fontSize:13.5,color:T.textMid,lineHeight:1.8,fontFamily:T.font}}>{service.prep_notes}</p>
              </Section>
            )}

            {/* Before/after portfolio — only if real tagged pairs exist. */}
            {beforeAfterPairs.length > 0 && (
              <Section id="portfolio" eyebrow="Proof of work" title="Before &amp; after">
                <BeforeAfterStrip pairs={beforeAfterPairs} />
              </Section>
            )}

            {isMobile && (
              <Section id="pro" eyebrow="Who's doing the work" title="About this pro">
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                  <Avatar name={proName} url={pro.profiles?.avatar_url} size={40} ring/>
                  <div><p style={{margin:0,fontSize:13.5,fontWeight:700,color:T.text,fontFamily:T.font}}>{proName}</p>{pro.member_since && <p style={{margin:"1px 0 0",fontSize:11,color:T.faint,fontFamily:T.font}}>Member since {pro.member_since}</p>}</div>
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:7, marginBottom:12 }}>
                  <VerifyRow label="Identity verified" ok={!!pro.verified}/>
                  <VerifyRow label="Background checked" ok={!!pro.background_checked}/>
                </div>
                <Btn v="outline" size="sm" full onClick={()=>navigate(`/pro/${pro.id}`)}>View full profile</Btn>
              </Section>
            )}

            {isMobile && <div style={{ marginBottom:12 }}><TrustScoreCard pro={pro} /></div>}

            <Section id="reviews" eyebrow="Client feedback" title="Reviews">
              {reviews.length > 0 ? (
                <>
                  <RatingBreakdown reviews={reviews} avgOverride={pro.avg_rating} countOverride={pro.review_count} />
                  <div style={{marginTop:18}}>{reviews.slice(0,6).map(r=><ReviewCard key={r.id} review={r}/>)}</div>
                  {reviews.length > 6 && <Btn v="ghost" size="sm" onClick={()=>navigate(`/pro/${pro.id}`)}>See all reviews on {proName.split(" ")[0]}'s profile</Btn>}
                </>
              ) : <p style={{fontSize:13,color:T.faint,fontFamily:T.font}}>No reviews yet. Be the first to book!</p>}
            </Section>
          </div>

          {isMobile && (
            <div style={{ display:"flex", flexDirection:"column", gap:14, marginBottom:12 }}>
              {[
                { icon:I.shield, t:"The JiraniApp Guarantee", s:"If something goes wrong, we make it right." },
                { icon:I.mpesa,  t:"Pay securely with M-Pesa", s:"Held safely, released once the job is confirmed done." },
              ].map((r,i)=>(
                <div key={i} style={{ display:"flex", gap:10, alignItems:"flex-start", background:T.white, border:`1px solid ${T.border}`, borderRadius:T.r14, padding:12 }}>
                  <div style={{ width:26,height:26,borderRadius:T.r8,background:T.greenTint,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,color:T.green }}>{r.icon}</div>
                  <div><p style={{ margin:0, fontSize:12, fontWeight:600, color:T.text, fontFamily:T.font }}>{r.t}</p><p style={{ margin:"1px 0 0", fontSize:11, color:T.faint, fontFamily:T.font, lineHeight:1.5 }}>{r.s}</p></div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* CUSTOMERS ALSO VIEWED */}
        {alsoViewed.length > 0 && (
          <div style={{marginTop:24}}>
            <EditorialHeading eyebrow="Keep browsing" title="Customers who viewed this also viewed" />
            <ScrollRow>{alsoViewed.map(item => <ListingCard key={`av_${item.id}`} item={item} navigate={navigate} />)}</ScrollRow>
          </div>
        )}

        {/* MORE FROM THIS PRO */}
        {moreFromPro.length > 0 && (
          <div style={{marginTop:24}}>
            <EditorialHeading eyebrow={`From ${proName.split(" ")[0]}`} title={`More services from ${proName.split(" ")[0]}`} />
            <ScrollRow>{moreFromPro.map(item => <ListingCard key={`mp_${item.id}`} item={item} navigate={navigate} />)}</ScrollRow>
          </div>
        )}

        {/* RELATED SEARCHES — dense SEO-style grid */}
        <div style={{marginTop:24,background:T.white,border:`1.5px solid ${T.border}`,borderRadius:T.r16,padding: isMobile ? "16px 14px" : "18px 20px"}}>
          <EditorialHeading eyebrow="Keep exploring" title="Related searches" sub="Popular services near you on JiraniApp" size="sm" />
          <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap:"6px 18px", marginTop:12 }}>
            {relatedSearches.map((t,i) => (
              <button key={i} onClick={()=>navigate(`/search?category=${encodeURIComponent(t.category)}&location=${encodeURIComponent(t.area)}`)} style={{
                textAlign:"left", background:"none", border:"none", cursor:"pointer", padding:"4px 0",
                fontSize:12, color:T.sub, fontFamily:T.font, textDecoration:"underline", textDecorationColor:T.border,
                overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
              }}
                onMouseEnter={e=>{ e.currentTarget.style.color=T.green; }}
                onMouseLeave={e=>{ e.currentTarget.style.color=T.sub; }}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* MOBILE CTA BAR — sticky, real price/selected package */}
      {isMobile && (
        <div style={{ position:"fixed",bottom:0,left:0,right:0,zIndex:200, background:"rgba(255,255,255,0.97)",backdropFilter:"blur(12px)", borderTop:`1px solid ${T.border}`,padding:"10px 16px", display:"flex",gap:8 }}>
          <button onClick={handleMessage} style={{ width:48,height:48,borderRadius:T.r12,flexShrink:0, border:`1px solid ${T.border}`,background:T.white, display:"flex",alignItems:"center",justifyContent:"center",color:T.sub }}>{I.msg}</button>
          <Btn v="primary" size="md" full onClick={()=>setShowModal(true)}>Book now · {fmtKsh(activePrice)}</Btn>
        </div>
      )}

      {showModal && <BookingModal service={service} price={activePrice} pro={pro} onClose={()=>setShowModal(false)} onDone={()=>setShowModal(false)} user={user} navigate={navigate} />}

      <div style={{ height: isMobile ? 64 : 0 }} />
      <Footer />
    </div>
  );
}

const CSS = `
  *, *::before, *::after { box-sizing: border-box; }
  button:focus-visible { outline: 2px solid #1e9254; outline-offset: 2px; }
  input:focus, select:focus, textarea:focus { border-color: #1e9254 !important; box-shadow: 0 0 0 3px rgba(30,146,84,0.08) !important; }
  .jp-scroll::-webkit-scrollbar { display: none; }
  .jp-scroll { scrollbar-width: none; -ms-overflow-style: none; }
  @keyframes ppPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
  @media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation: none !important; transition: none !important; } }
`;